import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebouncedValue } from '../../hooks';
import { HOME_SEARCH_DEBOUNCE_MS } from '../../constants';
import type { ParticipantTab } from '../../interfaces';
import {
  DEFAULT_PARTICIPANT_TAB,
  PARTICIPANT_PAGE_PARAM,
  PARTICIPANT_SEARCH_PARAM,
  PARTICIPANT_TAB_IDS,
  PARTICIPANT_TAB_PARAM,
} from '../constants';

const parseTab = (search: string): ParticipantTab => {
  const raw = new URLSearchParams(search).get(PARTICIPANT_TAB_PARAM);
  return PARTICIPANT_TAB_IDS.includes(raw as ParticipantTab)
    ? (raw as ParticipantTab)
    : DEFAULT_PARTICIPANT_TAB;
};

const parsePage = (search: string): number => {
  const raw = Number(new URLSearchParams(search).get(PARTICIPANT_PAGE_PARAM));
  return Number.isInteger(raw) && raw > 0 ? raw : 1;
};

const parseQuery = (search: string): string =>
  new URLSearchParams(search).get(PARTICIPANT_SEARCH_PARAM) ?? '';

const buildSearch = (tab: ParticipantTab, query: string, page: number): string => {
  const params = new URLSearchParams();
  params.set(PARTICIPANT_TAB_PARAM, tab);
  if (query) params.set(PARTICIPANT_SEARCH_PARAM, query);
  if (page > 1) params.set(PARTICIPANT_PAGE_PARAM, String(page));
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Tab + search + page state synced to ?tab=&q=&page=.
 * - Deep-linkable and back-button safe (the participant side of the
 *   outlet best-practice; path segments are owned by the admin tabs).
 * - Typing updates the input instantly; only the debounced query hits
 *   the network and the URL (history.replace, no entry spam).
 * - Tab switches use history.push and reset search + page.
 */
export const useParticipantTabs = () => {
  const history = useHistory();
  const location = useLocation();

  const [activeTab, setActiveTabState] = useState<ParticipantTab>(() => parseTab(location.search));
  const [searchInput, setSearchInputState] = useState<string>(() => parseQuery(location.search));
  const [page, setPageState] = useState<number>(() => parsePage(location.search));

  const debouncedSearch = useDebouncedValue(searchInput, HOME_SEARCH_DEBOUNCE_MS);

  const setActiveTab = useCallback(
    (tab: ParticipantTab) => {
      setActiveTabState(tab);
      setSearchInputState('');
      setPageState(1);
      history.push({ search: buildSearch(tab, '', 1) });
    },
    [history],
  );

  const setSearchTerm = useCallback((value: string) => {
    setSearchInputState(value);
    setPageState(1);
  }, []);

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  // Keep the URL in sync with debounced query + page (replace: no history spam).
  useEffect(() => {
    const next = buildSearch(activeTab, debouncedSearch, page);
    const current = location.search.startsWith('?') ? location.search : '';
    if (next !== current) {
      history.replace({ search: next });
    }
    // history/location intentionally excluded: syncing them back would loop.
  }, [activeTab, debouncedSearch, page]);

  return {
    activeTab,
    setActiveTab,
    searchInput,
    setSearchTerm,
    debouncedSearch,
    page,
    setPage,
  };
};
